import { CssBaseline, PaletteMode, ThemeProvider } from "@mui/material";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { createAppTheme } from "../theme";

const DARK_THEME_STORAGE_KEY = "sky-view:preferences:darkTheme";

type ThemePreferenceContextValue = {
  darkThemeEnabled: boolean;
  setDarkThemeEnabled: (enabled: boolean) => void;
};

const ThemePreferenceContext = createContext<ThemePreferenceContextValue | undefined>(
  undefined,
);

const readStoredPreference = (): boolean => {
  if (typeof window === "undefined") {
    return true;
  }

  const stored = window.localStorage.getItem(DARK_THEME_STORAGE_KEY);
  if (stored === null) {
    return true;
  }

  return stored !== "false";
};

export const ThemePreferenceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [darkThemeEnabled, setDarkThemeEnabled] = useState<boolean>(
    readStoredPreference,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      DARK_THEME_STORAGE_KEY,
      darkThemeEnabled ? "true" : "false",
    );
  }, [darkThemeEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handler = (event: StorageEvent) => {
      if (event.key === DARK_THEME_STORAGE_KEY) {
        if (event.newValue === null) {
          setDarkThemeEnabled(true);
          return;
        }
        setDarkThemeEnabled(event.newValue !== "false");
      }
    };

    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const themeMode: PaletteMode = darkThemeEnabled ? "dark" : "light";

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  const value = useMemo(
    () => ({ darkThemeEnabled, setDarkThemeEnabled }),
    [darkThemeEnabled],
  );

  return (
    <ThemePreferenceContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemePreferenceContext.Provider>
  );
};

export const useThemePreference = () => {
  const context = useContext(ThemePreferenceContext);
  if (!context) {
    throw new Error(
      "useThemePreference must be used within a ThemePreferenceProvider",
    );
  }
  return context;
};
